import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle, PaperPlaneTilt, ArrowClockwise, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Small status card for the daily 00:05 IST ledger broadcast — shows whether
 * today's cron already ran and offers a "Send now" fallback for cases where
 * a restart or sleep-drift caused the scheduler to miss its minute.
 *
 * v1.31.10 — Collapsed to a single summary row by default (last run · sent ·
 * failed). Click to expand full details. Auto-expands when failed_count > 0.
 */
export default function DailyBroadcastCard() {
    const [status, setStatus] = useState(null);
    const [log, setLog] = useState(null);
    const [running, setRunning] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const load = async () => {
        try {
            const [s, l] = await Promise.allSettled([
                api.get("/admin/daily-broadcast-status"),
                api.get("/admin/daily-ledger-log"),
            ]);
            if (s.status === "fulfilled") setStatus(s.value.data);
            if (l.status === "fulfilled") setLog(l.value.data);
        } catch { /* silent */ }
    };
    useEffect(() => {
        load();
        const id = setInterval(load, 60 * 1000);
        return () => clearInterval(id);
    }, []);

    const run = async () => {
        if (!window.confirm(
            `Send today's daily ledger broadcast now to ${status?.recipient || "admin"}?\n\nThis will email you the ledger for every active party.`
        )) return;
        setRunning(true);
        try {
            const { data } = await api.post("/admin/run-daily-broadcast");
            toast.success(`Sent ${data.sent_count} · Failed ${data.failed_count}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Broadcast failed");
        } finally {
            setRunning(false);
        }
    };

    if (!status) return null;
    const ok = status.ran_today && status.sent_count > 0;
    const warn = !status.ran_today || status.failed_count > 0;
    const autoExpand = (status.failed_count || 0) > 0; // failures always surface expanded
    const open = expanded || autoExpand;
    const lastRun = status.last_run_at
        ? (() => { const d = new Date(status.last_run_at); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())} IST`; })()
        : "—";

    return (
        <div
            data-testid="daily-broadcast-card"
            className={`rounded-xl border shadow-sm ${warn ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50/60"}`}
        >
            {/* Collapsed summary row — always visible, click to expand */}
            <button
                type="button"
                data-testid="broadcast-toggle"
                onClick={() => setExpanded((v) => !v)}
                className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left"
            >
                {ok
                    ? <CheckCircle size={18} weight="fill" className="shrink-0 text-emerald-600" />
                    : <WarningCircle size={18} weight="fill" className="shrink-0 text-amber-700" />
                }
                <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${warn ? "text-amber-800" : "text-emerald-800"}`}>
                    Daily Ledger Broadcast
                </span>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                    <span data-testid="broadcast-last-run">Last run <span className="font-semibold text-slate-800">{lastRun}</span></span>
                    <span>Sent <span data-testid="broadcast-sent" className="font-semibold text-emerald-700">{status.sent_count || 0}</span></span>
                    <span>Failed <span data-testid="broadcast-failed" className={`font-semibold ${(status.failed_count || 0) > 0 ? "text-rose-700" : "text-slate-700"}`}>{status.failed_count || 0}</span></span>
                </span>
                <CaretDown size={14} className={`ml-auto shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div data-testid="broadcast-details" className="border-t border-slate-200/70 px-4 pb-4 pt-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className={`font-display text-base font-bold ${warn ? "text-amber-900" : "text-emerald-900"}`}>
                                {ok
                                    ? `Sent ${status.sent_count} ledger${status.sent_count === 1 ? "" : "s"} today`
                                    : status.ran_today
                                        ? `Ran, but ${status.failed_count} failed`
                                        : "Not run yet today"
                                }
                            </div>
                            <div className="mt-0.5 text-[11px] text-slate-600">
                                To <span className="font-mono">{status.recipient}</span>
                                {status.last_run_at && <> · last run {lastRun}</>}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                data-testid="reload-broadcast-status"
                                variant="outline"
                                size="sm"
                                onClick={load}
                                className="h-8 border-slate-300 text-xs"
                            >
                                <ArrowClockwise size={12} className="mr-1" /> Refresh
                            </Button>
                            <Button
                                data-testid="run-broadcast-now"
                                onClick={run}
                                disabled={running}
                                size="sm"
                                className={`h-8 text-xs text-white ${warn ? "bg-amber-700 hover:bg-amber-800" : "bg-emerald-700 hover:bg-emerald-800"}`}
                            >
                                <PaperPlaneTilt size={12} className="mr-1" />
                                {running ? "Sending…" : "Send today's now"}
                            </Button>
                        </div>
                    </div>
                    {log && (log.total_attempts > 0 || (log.duplicate_send_pairs || []).length > 0) && (
                        <div data-testid="daily-ledger-log-summary" className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-white/70 p-2.5 text-[11px] sm:grid-cols-4">
                            <div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Sent</div>
                                <div className="mt-0.5 font-mono text-sm font-bold text-emerald-700" data-testid="log-sent-count">{log.sent_count || 0}</div>
                            </div>
                            <div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Failed</div>
                                <div className="mt-0.5 font-mono text-sm font-bold text-rose-700" data-testid="log-failed-count">{log.failed_count || 0}</div>
                            </div>
                            <div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Skipped Dup</div>
                                <div className="mt-0.5 font-mono text-sm font-bold text-violet-700" data-testid="log-skipped-count">{log.skipped_duplicate_count || 0}</div>
                            </div>
                            <div>
                                <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Dup Sent Pairs</div>
                                <div className={`mt-0.5 font-mono text-sm font-bold ${(log.duplicate_send_pairs || []).length > 0 ? "text-rose-700" : "text-slate-500"}`} data-testid="log-dup-pairs-count">{(log.duplicate_send_pairs || []).length}</div>
                            </div>
                            {(log.duplicate_send_pairs || []).length > 0 && (
                                <div className="col-span-2 sm:col-span-4 mt-1 rounded border border-rose-200 bg-rose-50 p-2 text-[10px] text-rose-800">
                                    <div className="font-bold uppercase tracking-wider">⚠ Legacy dup sends (before v1.28.91 fix):</div>
                                    <ul className="mt-1 space-y-0.5 font-mono">
                                        {(log.duplicate_send_pairs || []).slice(0, 5).map((d, i) => (
                                            <li key={i}>
                                                {(d.sent_rows || []).length}× → {d.recipient} · trig={d.trigger} · balances=[{(d.sent_rows || []).map(r => r.closing_balance ?? "—").join(", ")}]
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
